import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/client";
import { formatDateLong, formatDate, todayISO } from "../../lib/date";
import { isDirectorateScoped, myDirectorateReportPath } from "../../lib/directorateScope";

const NAV_CARDS = [
  {
    to:      "/dashboard",
    title:   "لوحة المعلومات",
    desc:    "نظرة شاملة: اتجاه الوجبات، توزيع الأصناف، ونسبة التغطية مقابل الأعداد",
    color:   "📊",
    roles:   null,
  },
  {
    to:      "/meal-entry",
    title:   "طلب وجبات",
    desc:    "إرسال طلب الوجبات والمواد ليوم التنفيذ قبل موعد الإقفال",
    color:   "📝",
    roles:   ["DATA_ENTRY","REVIEWER","SYSTEM_ADMIN"],
  },
  {
    to:      "/all-data",
    title:   "كل الطلبات",
    desc:    "عرض جميع الطلبات اليومية ضمن أي مدى تاريخي",
    color:   "📋",
    roles:   null,
  },
  {
    to:      "/summary/directorates",
    title:   "ملخص المديريات والأفرع",
    desc:    "مجاميع الوجبات لكل مديرية/فرع/قسم",
    color:   "🏛",
    roles:   null,
  },
  {
    to:      "/summary/governorates",
    title:   "ملخص المحافظات",
    desc:    "مجاميع الوجبات حسب المحافظة السورية",
    color:   "🗺",
    roles:   null,
  },
  {
    to:      "/headcount",
    title:   "أعداد الكيانات",
    desc:    "العدد الكامل والمبدل والباقي لكل مديرية وفرع",
    color:   "👥",
    roles:   ["REVIEWER","SYSTEM_ADMIN"],
  },
  {
    to:      "/dictionaries",
    title:   "القواميس",
    desc:    "المحافظات والمديريات والمطاعم وفئات الأصناف",
    color:   "📚",
    roles:   ["SYSTEM_ADMIN"],
  },
  {
    to:      "/users",
    title:   "المستخدمون",
    desc:    "إدارة المستخدمين والصلاحيات",
    color:   "👤",
    roles:   ["SYSTEM_ADMIN"],
  },
  {
    to:      "/audit",
    title:   "سجل التدقيق",
    desc:    "متابعة جميع العمليات والتغييرات",
    color:   "🔍",
    roles:   ["SYSTEM_ADMIN"],
  },
  {
    to:      "/settings",
    title:   "الإعدادات",
    desc:    "موعد إقفال الطلبات وقواعد الطلب المسبق",
    color:   "⚙️",
    roles:   ["SYSTEM_ADMIN"],
  },
];

export default function HomePage() {
  const { user, isRole } = useAuth();

  // أقرب يوم مفتوح للطلب وموعد إقفاله — من الخادم
  const { data: open } = useQuery({
    queryKey: ["open-dates"],
    staleTime: 60_000,
    enabled: isRole("DATA_ENTRY", "REVIEWER", "SYSTEM_ADMIN"),
    queryFn: () =>
      api.get<{ data: { dates: Array<{ date: string; allowed: boolean; deadline: string }> } }>(
        "/meal-entries/open-dates"
      ).then((r) => r.data.data),
  });
  const next = open?.dates.find((d) => d.allowed);

  const scoped = isDirectorateScoped(user);
  const reportPath = myDirectorateReportPath(user);

  const visibleCards = NAV_CARDS.filter((card) => {
    if (card.roles && !card.roles.some((r) => isRole(r as "SYSTEM_ADMIN"))) return false;
    if (scoped && (card.to === "/summary/directorates" || card.to === "/summary/governorates")) {
      return false;
    }
    return true;
  });

  const homeCards = reportPath
    ? [
        ...visibleCards.slice(0, 2),
        {
          to: reportPath,
          title: "تقرير مديريتي",
          desc: "ملخص وجبات مديريتك عبر المحافظات — لا تظهر مديريات أخرى",
          color: "🏛",
          roles: null as string[] | null,
        },
        ...visibleCards.slice(2),
      ]
    : visibleCards;

  return (
    <div>
      {/* بطاقة الترحيب */}
      <div className="card accent-top mb-8 flex items-center gap-5">
        <img src="/logo.png" alt="" className="w-20 h-16 object-contain shrink-0" />
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-gray-900">طلبات وجبات الطعام</h1>
          <p className="text-sand-500 text-sm">
            الجمهورية العربية السورية — وزارة الدفاع — الإدارة العامة — فرع الشؤون الإدارية
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-6 text-sm text-sand-700">
            <span>
              التاريخ: {formatDateLong(todayISO())}
            </span>
            {user && (
              <span>مرحباً، {user.fullName}</span>
            )}
          </div>
        </div>
      </div>

      {/* شبكة التنقل */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {homeCards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className="group card accent-top block !p-4 hover:border-primary-700 hover:-translate-y-0.5 transition"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="w-10 h-10 rounded-lg bg-sand-50 border border-sand-300 flex items-center justify-center text-lg">{card.color}</span>
              <h3 className="font-bold text-sm text-gray-900 group-hover:text-primary-700">{card.title}</h3>
            </div>
            <p className="text-sand-500 text-xs leading-relaxed">{card.desc}</p>
          </Link>
        ))}
      </div>

      {/* الطلب القادم */}
      {next && (
        <div className="mt-8 card">
          <h2 className="text-base font-semibold text-gray-700 mb-2">الطلب القادم</h2>
          <p className="text-sm text-gray-600">
            يوم التنفيذ: <strong dir="ltr">{formatDate(next.date)}</strong>
            <span className="text-gray-400 mx-2">—</span>
            آخر موعد للإرسال: <strong dir="ltr">{next.deadline}</strong>
          </p>
          <div className="mt-3 flex gap-2">
            <Link to="/meal-entry" className="btn-primary text-xs px-3 py-1.5">إرسال الطلب</Link>
            <Link to={reportPath ?? "/summary/directorates"} className="btn-secondary text-xs px-3 py-1.5">
              {scoped ? "ما طلبته مديريتي" : "عرض الملخص"}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
